import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import LabelList from '../utils/labelList';
import { Input, Select, Switch, Form } from 'antd';
import { Box, Typography, Stack } from '@mui/material';
import ActionButton from 'components/button/ActionButton';
import SmoothDrawerContainer from './SmoothDrawerContainer';
import SolutionConfiguration from './SolutionConfiguration';
import ConditionConfiguration from './ConditionConfiguration';
import SolutionTaskConfiguration from './SolutionTaskConfiguration';

const { Option } = Select;

const NodeConfigurationDrawer = ({ nodes, edges, node, activationStatus, disabled = false, setNodes, setEdges, onClose }) => {
	const [form] = Form.useForm();
	const deleteBtnRef = useRef(null);

	const solutionData = nodes?.find((item) => item?.type === 'solution')?.data ?? null;
	const { creatorOrganizationId, userId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;

	const handleDelete = () => {
		const edgeData = edges?.find((item) => item?.target === node.id) ?? null;

		if (edgeData) {
			const updateNodeList = nodes.map((node) => {
				if (node.id === edgeData.source) {
					return {
						...node,
						data: {
							...node.data,
							hasChild: false,
						},
					};
				} else {
					return node;
				}
			});

			const filterNodeList = updateNodeList.filter((n) => n.id !== node.id);

			setNodes(filterNodeList);
		} else {
			// Delete node
			setNodes((nds) => nds.filter((n) => n.id !== node.id));
		}

		// Delete connected edges
		setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));

		onClose();
	};

	const handleSubmitConfiguration = (values) => {
		const payload = {
			isValid: true,
			mode: values?.mode,
			note: values?.note,
			label: values?.label,
			locked: values?.locked,
			modePosition: values?.modePosition,
		};

		if (node?.type === 'solution') {
			payload.solution = {
				name: values?.name,
				// activationStatus: '',
				module: values?.module,
				companyCode: companyCode,
				vertical: values?.vertical,
				subModule: values?.subModule,
				solutionId: values?.solutionId,
				verticalName: values?.verticalName,
				shortDescription: values?.shortDescription,
				createdBy: values?.createdBy ? values?.createdBy : userId,
				modifiedBy: values?.modifiedBy ? values?.modifiedBy : userId,
			};
		} else if (node?.type === 'solution_task') {
			payload.solutionTask = {
				type: values?.type,
				taskCode: values?.taskCode,
				taskRole: values?.taskRole,
				actionName: values?.actionName,
				companyCode: values?.companyCode,
				taskGroupCode: values?.taskGroupCode,
				solutionTaskId: values?.solutionTaskId,
				taskActionTaken: values?.taskActionTaken,
				templatenamePropertyCodes: values?.templatenamePropertyCodes,
			};
		} else if (node?.type === 'condition') {
			payload.conditionId = values?.conditionId;
			payload.taskActionTaken = values?.taskActionTaken;
			payload.solutionTaskCode = values?.solutionTaskCode;
			payload.conditionPeopleSet = values?.conditionPeopleSet;
		}

		const locked = values?.locked ? true : false;

		setNodes((nodes) => {
			const currentIndex = nodes.findIndex((n) => n.id === node.id);

			let updatedNodes = [...nodes];

			updatedNodes = updatedNodes.map((n) =>
				n.id === node.id
					? {
							...n,
							data: { ...n.data, ...payload },
							draggable: !locked,
							connectable: !locked,
						}
					: n
			);

			if (currentIndex > 0 && nodes[currentIndex].type === 'solution_task' && nodes[currentIndex - 1].type === 'condition') {
				const prevNode = nodes[currentIndex - 1];

				updatedNodes[currentIndex - 1] = {
					...prevNode,
					data: {
						...prevNode.data,
						taskActionTaken: payload?.solutionTask?.taskActionTaken ?? '',
					},
				};
			}

			return updatedNodes;
		});

		onClose();
	};

	useEffect(() => {
		if (node?.data) {
			const nodeData = node?.data;

			form?.setFieldsValue({
				mode: nodeData?.mode,
				note: nodeData?.note,
				processCode: nodeData?.processCode,
				modePosition: nodeData?.modePosition,
				locked: nodeData?.locked ? true : false,
				processVersion: nodeData?.processVersion,
				isValid: nodeData?.isValid ? true : false,
				label: LabelList?.[nodeData?.label] ?? nodeData?.label,

				name: nodeData?.solution?.name,
				module: nodeData?.solution?.module,
				vertical: nodeData?.solution?.vertical,
				subModule: nodeData?.solution?.subModule,
				createdBy: nodeData?.solution?.createdBy,
				modifiedBy: nodeData?.solution?.modifiedBy,
				solutionId: nodeData?.solution?.solutionId,
				activationStatus: nodeData?.solution?.activationStatus,
				shortDescription: nodeData?.solution?.shortDescription,

				conditionId: nodeData?.conditionId,
				solutionTaskCode: nodeData?.solutionTaskCode,
				conditionPeopleSet: nodeData?.conditionPeopleSet,

				type: nodeData?.solutionTask?.type,
				taskCode: nodeData?.solutionTask?.taskCode,
				taskRole: nodeData?.solutionTask?.taskRole,
				taskGroupCode: nodeData?.solutionTask?.taskGroupCode,
				solutionTaskId: nodeData?.solutionTask?.solutionTaskId,
				companyCode: nodeData?.solutionTask?.companyCode ?? companyCode,
				actionName: nodeData?.solutionTask?.actionName ?? 'Participated',
				templatenamePropertyCodes: nodeData?.solutionTask?.templatenamePropertyCodes,

				taskActionTaken: node?.type === 'condition' ? nodeData?.taskActionTaken : nodeData?.solutionTask?.taskActionTaken,
			});
		}
	}, [node]);

	useEffect(() => {
		console.log('deleteBtnRef.current', deleteBtnRef.current?.focus());

		deleteBtnRef.current?.focus();
	}, []);

	return (
		<SmoothDrawerContainer open={true} onClose={onClose}>
			<Form disabled={disabled} form={form} layout="vertical" onFinish={handleSubmitConfiguration}>
				<Box mb={2}>
					<Typography fontSize={14} fontWeight={500} mb={0.5}>
						Label
					</Typography>

					<Form.Item style={{ marginBottom: 0 }} name="label">
						<Input placeholder="Enter node label" disabled />
					</Form.Item>
				</Box>

				<Box mb={2}>
					<Typography fontSize={14} fontWeight={500} mb={0.5}>
						Mode
					</Typography>

					<Form.Item style={{ marginBottom: 0 }} name="mode">
						<Select style={{ width: '100%' }} getPopupContainer={(trigger) => trigger.parentNode}>
							<Option value="input">Input</Option>
							<Option value="output">Output</Option>
							<Option value="both">Input & Output</Option>
						</Select>
					</Form.Item>
				</Box>

				{/* <Box mb={2}>
					<Typography fontSize={14} fontWeight={500} mb={0.5}>
						Mode Position
					</Typography>

					<Form.Item style={{ marginBottom: 0 }} name="modePosition">
						<Select style={{ width: '100%' }} getPopupContainer={(trigger) => trigger.parentNode}>
							<Option value="top_bottom">Top & Bottom</Option>
							<Option value="left_right">Left & Right</Option>
						</Select>
					</Form.Item>
				</Box> */}

				<Form.Item noStyle name="isValid" />
				<Form.Item noStyle name="processCode" />
				<Form.Item noStyle name="processVersion" />

				{node?.type === 'solution' && <SolutionConfiguration form={form} />}

				{node?.type === 'solution_task' && <SolutionTaskConfiguration solutionData={solutionData} form={form} disabled={disabled} />}

				{node?.type === 'condition' && <ConditionConfiguration form={form} disabled={disabled} />}

				{node?.type !== 'solution' && (
					// <Box mb={2}>
					// 	<Typography fontSize={14} fontWeight={500} mb={0.5}>
					// 		Note
					// 	</Typography>

					<Form.Item
						noStyle
						name="note"
						style={{ marginBottom: 0 }}
						rules={[
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || value.length <= 100) {
										return Promise.resolve();
									}
									return Promise.reject('Note cannot exceed 100 characters');
								},
							}),
						]}
					>
						{/* <Input placeholder="Enter node note" /> */}
					</Form.Item>
					// </Box>
				)}

				<Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
					<Typography fontSize={14} fontWeight={500}>
						Locked
					</Typography>

					<Form.Item style={{ marginBottom: 0 }} name="locked">
						<Switch />
					</Form.Item>
				</Box>

				{!disabled && (
					<Stack direction="row" justifyContent="flex-end" sx={{ mt: 3, gap: '12px' }}>
						{node?.type !== 'solution' && !(activationStatus?.toLowerCase() === 'active' && node?.type === 'condition') && (
							<ActionButton
								label="Delete"
								tooltip="Delete"
								ref={deleteBtnRef}
								variantType="reject"
								onClick={handleDelete}
								onKeyDown={(e) => {
									console.log('e.key', e.key);

									if (e.key === 'Delete') {
										handleDelete();
									}
								}}
							/>
						)}

						<ActionButton type="submit" tooltip="Save" variantType="save" label="Save" />
					</Stack>
				)}
			</Form>
		</SmoothDrawerContainer>
	);
};

export default NodeConfigurationDrawer;
